import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Stack, 
  Chip, 
  Avatar,
  Divider,
  CircularProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import RefreshIcon from '@mui/icons-material/Refresh';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import ErrorIcon from '@mui/icons-material/Error';
import DevicesIcon from '@mui/icons-material/Devices';
import api from '../api';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/pl';
import 'dayjs/locale/en';

dayjs.extend(relativeTime);

/**
 * ActivityFeed Component
 * Displays a real-time activity feed showing recent sessions, alerts, and device activities
 */
function ActivityFeed({ deviceId = null, limit = 20, autoRefresh = false, refreshInterval = 30000 }) {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const { t, i18n } = useTranslation();

  // Set dayjs locale based on current language
  dayjs.locale(i18n.language);

  const fetchActivities = React.useCallback(async () => {
    try {
      const params = { limit };
      if (deviceId) {
        params.device_id = deviceId;
      }

      const response = await api.get('/api/activity-feed/', { params });
      setActivities(response.data.activities || []);
    } catch (error) {
      console.error('Error fetching activity feed:', error);
    } finally {
      setLoading(false);
    }
  }, [deviceId, limit]);

  useEffect(() => {
    fetchActivities();

    // Set up auto-refresh if enabled
    if (autoRefresh) {
      const interval = setInterval(fetchActivities, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [fetchActivities, autoRefresh, refreshInterval]);

  const handleRefresh = () => {
    setLoading(true);
    fetchActivities();
  };

  const getActivityIcon = (type, level) => {
    const iconProps = { fontSize: 'small' };
    
    switch (type) {
      case 'high_stress_alert':
        return <ErrorIcon {...iconProps} />;
      case 'moderate_stress_alert':
        return <WarningAmberIcon {...iconProps} />;
      case 'session_started':
        return <CheckCircleIcon {...iconProps} />;
      default:
        return <InfoIcon {...iconProps} />;
    }
  };

  const getActivityColor = (level) => {
    switch (level) {
      case 'danger':
        return theme.palette.error.main;
      case 'warning':
        return theme.palette.warning.main;
      case 'success':
        return theme.palette.success.main;
      default:
        return theme.palette.info.main;
    }
  };

  const getActivityLabel = (type) => {
    switch (type) {
      case 'high_stress_alert':
        return t('activity_feed.high_stress_alert', 'High Stress Alert');
      case 'moderate_stress_alert':
        return t('activity_feed.moderate_stress_alert', 'Moderate Stress');
      case 'session_started':
        return t('activity_feed.session_started', 'Session Started');
      default:
        return t('activity_feed.activity', 'Activity');
    }
  };

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  if (loading && activities.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <DevicesIcon color="primary" />
            <Typography variant="h6">
              {t('activity_feed.title', 'Activity Feed')}
            </Typography>
          </Stack>
          <Tooltip title={t('activity_feed.refresh', 'Refresh')}>
            <IconButton size="small" onClick={handleRefresh} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>

        {activities.length === 0 ? (
          <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
            {t('activity_feed.no_activities', 'No recent activities')}
          </Typography>
        ) : (
          <Stack spacing={2} divider={<Divider />}>
            {activities.map((activity, index) => (
              <Stack key={activity.id} spacing={1}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Avatar
                    sx={{
                      bgcolor: getActivityColor(activity.level),
                      width: 32,
                      height: 32,
                    }}
                  >
                    {getActivityIcon(activity.type, activity.level)}
                  </Avatar>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Typography variant="body2" fontWeight="500">
                        {activity.device_name}
                      </Typography>
                      <Chip
                        label={getActivityLabel(activity.type)}
                        size="small"
                        sx={{
                          bgcolor: `${getActivityColor(activity.level)}20`,
                          color: getActivityColor(activity.level),
                          fontWeight: 500,
                        }}
                      />
                    </Stack>

                    <Typography variant="caption" color="text.secondary">
                      {activity.details?.title || t('activity_feed.session', 'Session')}
                    </Typography>

                    <Stack direction="row" spacing={2} mt={0.5} flexWrap="wrap">
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <AccessTimeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {formatDuration(activity.duration)}
                        </Typography>
                      </Stack>

                      {activity.stress_level > 0 && (
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: activity.stress_level > 50 ? 'error.main' : 'warning.main',
                            fontWeight: 500 
                          }}
                        >
                          {t('activity_feed.stress', 'Stress')}: {activity.stress_level.toFixed(0)}%
                        </Typography>
                      )}

                      <Typography variant="caption" color="text.secondary">
                        {dayjs(activity.timestamp).fromNow()}
                      </Typography>
                    </Stack>
                  </Box>
                </Stack>
              </Stack>
            ))}
          </Stack>
        )}

        {autoRefresh && (
          <Typography 
            variant="caption" 
            color="text.secondary" 
            align="center" 
            display="block" 
            mt={2}
          >
            {t('activity_feed.auto_refresh', 'Auto-refreshing every {{seconds}} seconds', { 
              seconds: refreshInterval / 1000 
            })}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

export default ActivityFeed;
